<script setup lang="ts">
    import { computed, ref } from 'vue';
    import { requireCanEditCode } from '@/shared/directives/requirePermission';
    import { PipingClassSizeGenericView } from '@/apps/STID_Pipe/pipingClass.ts';
    import { usePipeClassStore } from '@/stores/pipeClassStore.ts';
    import PipeClassSizeSelector from '@/apps/STID_Pipe/PipeClassSizeSelector.vue';
    import PipeClassSelector from '@/apps/STID_Pipe/PipeClassSelector.vue';
    import PipingClassSizeEdit, { CodeProp } from '@/apps/STID_Pipe/PipingClassSizeEdit.vue';

    const vRequireCanEditCode = requireCanEditCode;

    const pipeClassStore = usePipeClassStore();
    const isFormActive = ref<boolean>(false);
    const isEditing = computed<boolean>(() => {
        return isFormActive.value && !!pipeClassStore.selectedPipingClassSize;
    });

    const code = ref<CodeProp | null>(null);

    const newClicked = ref<boolean>(false);

    // Used to force a reload of the edit component
    const formKey = ref<number>(0);

    const codeEditTitle = computed<string>(() => {
        const verb = isEditing.value ? 'Edit' : 'New';
        return `${verb} Piping Class Size`;
    });

    const isCreating = computed<boolean>(() => {
        return isFormActive.value && !pipeClassStore.selectedPipingClassSize;
    });

    const existsCodeSetForScope = computed<boolean>(() => {
        return !pipeClassStore.loading.pipingClassSizeCodeSetName && pipeClassStore.currentPipingClassSizeCodeSetName !== null;
    });

    function onNewPipingClassSizeClicked() {
        newClicked.value = true;
        if (pipeClassStore.selectedPipingClassSize === null) {
            // we manually need to call the selected handler if no pipe class size was selected when clicking new
            onPipingClassSizeSelected(null);
        } else {
            pipeClassStore.setPipingClassSize(null);
        }
    }

    function onCancelClicked() {
        pipeClassStore.setPipingClassSize(null);
        isFormActive.value = false;
    };

    async function onSavedPipingClassSize() {
        const newSizes = await pipeClassStore.loadPipingClassSizes(true, true);
        if (newSizes && newSizes.length > 0) {
            pipeClassStore.setPipingClassSize(newSizes[0].Id);
            code.value = {
                id: newSizes[0].Id,
                isNew: false
            };
        }
        formKey.value++;
    }

    function onPipingClassSizeSelected(newValue: PipingClassSizeGenericView | null) {
        if (newValue === null && !newClicked.value) {
            code.value = null;
            return;
        }
        if (newValue === null) {
            code.value = {
                isNew: true,
                id: null
            };
        } else {
            code.value = {
                isNew: false,
                id: newValue.Id
            };
        }
        newClicked.value = false;
        formKey.value++;
        isFormActive.value = true;
    }

    function onPipingClassSelected() {
        isFormActive.value = false;
    }

</script>
<template>
    <div class="columns">
        <div class="column is-narrow">
            <div class="box">
                <div class="columns">
                    <div class="column">
                        <div class="min-h-3">
                            <b-button
                                v-require-can-edit-code="{ libraryName: 'PipingClassSize', scopes: pipeClassStore.selectedScope ? [pipeClassStore.selectedScope] : [] }"
                                class="level-item"
                                type="is-info"
                                :disabled="!(pipeClassStore.selectedScope && pipeClassStore.selectedPipingClass && existsCodeSetForScope)"
                                :model-value="isCreating"
                                @click="onNewPipingClassSizeClicked">
                                <span class="icon is-small">
                                    <i
                                        class="mdi mdi-plus"
                                        aria-hidden="true" />
                                </span>
                                <span>
                                    New Piping Class Size
                                </span>
                            </b-button>
                        </div>
                        <pipe-class-selector
                            @modified="onPipingClassSelected" />
                    </div>
                    <div class="column">
                        <div class="min-h-3">
                            <div
                                class="subtitle">
                                <strong v-if="pipeClassStore.selectedPipingClass">
                                    Piping class: {{ pipeClassStore.selectedPipingClass.Name }}
                                </strong>
                                <strong v-else />
                            </div>
                        </div>
                        <pipe-class-size-selector @modified="onPipingClassSizeSelected" />
                    </div>
                </div>
            </div>
        </div>
        <div
            v-if="isFormActive"
            class="column">
            <div class="box">
                <div v-if="existsCodeSetForScope">
                    <h1 class="title center-large-text">
                        {{ codeEditTitle }}
                    </h1>
                    <template
                        v-if="code && !pipeClassStore.loading.any">
                        <piping-class-size-edit
                            :key="formKey"
                            :code="code"
                            @refresh="onSavedPipingClassSize"
                            @cancel="onCancelClicked" />
                    </template>
                </div>
                <b-message
                    v-else
                    type="is-danger"
                    has-icon>
                    Missing code set
                </b-message>
            </div>
        </div>
    </div>
</template>
<style scoped>
.min-h-3 {
    min-height: 3rem
}
</style>
